import { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import ErrorIcon from "@material-ui/icons/Error";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Caption } from "../Typography";
import { colorsLight } from "../../themes";
import { EaseScoreThresholds, getDenialReadingLevel } from "../../util/flesch-kincaid/fleschKincaidUtils";
import { ReadingLevelsModal } from "./ReadingLevelsModal";

interface ReadingLevelChipProps {
  text: string;
  thresholds?: EaseScoreThresholds;
}

const STATUS_ICONS = {
  meets: CheckIcon,
  almostMeets: ErrorIcon,
  doesNotMeet: CloseIcon,
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0.75),
    borderRadius: theme.spacing(0.5),
    border: "1px solid",
    cursor: "default",
  },
  meets: {
    color: colorsLight.success.dark,
    borderColor: colorsLight.success.dark,
    backgroundColor: colorsLight.success.light,
  },
  almostMeets: {
    color: colorsLight.warning.main,
    borderColor: colorsLight.warning.main,
    // the design's warning background; the theme's warning.light (#FAF2E6) is a different shade
    backgroundColor: "#FFF5E5",
  },
  doesNotMeet: {
    color: colorsLight.error.dark,
    borderColor: colorsLight.error.dark,
    backgroundColor: colorsLight.error.light,
  },
  icon: {
    height: 16,
    width: 16,
  },
  label: {
    color: "inherit",
    fontFamily: "Figtree",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "120%",
  },
  infoButton: {
    padding: 0,
    color: colorsLight.font.tertiaryAlt,
  },
}));

export function ReadingLevelChip({ text, thresholds }: ReadingLevelChipProps) {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const warningEaseScore = thresholds?.warningEaseScore;
  const errorEaseScore = thresholds?.errorEaseScore;
  const readingLevel = useMemo(
    () => getDenialReadingLevel(text, { warningEaseScore, errorEaseScore }),
    [text, warningEaseScore, errorEaseScore]
  );
  if (!readingLevel) {
    return null;
  }
  const { label, easeScore, status } = readingLevel;
  const Icon = STATUS_ICONS[status];
  return (
    <div className={classes.container}>
      <span className={`${classes.chip} ${classes[status]}`} data-testid="reading-level-chip" data-status={status}>
        <Icon className={classes.icon} />
        <Caption className={classes.label}>{`${label}: ${easeScore}`}</Caption>
      </span>
      <IconButton
        className={classes.infoButton}
        aria-label="View Flesch-Kincaid reading levels"
        onClick={() => setModalOpen(true)}
      >
        <InfoOutlinedIcon className={classes.icon} />
      </IconButton>
      <ReadingLevelsModal open={modalOpen} onClose={() => setModalOpen(false)} easeScore={easeScore} />
    </div>
  );
}
